import { useState } from "react";
import { Link } from "react-router";
import { AuthLayout } from "@/components/auth-layout";
import { Icon } from "@/components/icons";
import { Form, FormField, FormSubmit } from "@/src/forms";
import { requestPasswordReset } from "@/src/services/auth_service";
import { forgotPasswordSchema, type ForgotPasswordValues } from "./_schemas/forgot-password";

export default function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleValid(values: ForgotPasswordValues) {
    setError(null);
    // Contract endpoint: POST /api/users/password-reset/ JSON { email }. The reset
    // link in the email is assumed to land on our /reset-password?uid=&token=
    // (open Q to Alejandro) where ResetPasswordPage calls confirmPasswordReset.
    // We only fire the request here. Always shows "sent" on success — the backend
    // does not reveal whether the email exists.
    const res = await requestPasswordReset(values.email);
    if (!res.ok) {
      setError(res.error.message);
      return;
    }
    setSent(true);
  }

  return (
    <AuthLayout>
      <div className="popup w-[400px] rounded-[8px]">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <h1 className="text-h4 text-black">Reset your password</h1>
            <p className="text-small text-grey-900">
              {sent
                ? "Check your inbox — we sent you a link to reset your password."
                : "Enter your email and we'll send you a link to reset your password."}
            </p>
          </div>

          {!sent ? (
            <Form
              schema={forgotPasswordSchema}
              defaultValues={{ email: "" }}
              onSubmit={handleValid}
              className="flex flex-col gap-4"
            >
              <FormField
                name="email"
                label="Email"
                type="email"
                autoComplete="email"
                placeholder="username@example.com"
              />

              {error && (
                <div
                  role="alert"
                  className="rounded-[4px] border border-red-500 bg-red-100 px-3 py-2 text-small text-red-600"
                >
                  {error}
                </div>
              )}

              <FormSubmit
                hierarchy="primary"
                size="md"
                className="w-full"
                pendingLabel="Sending…"
                rightIcon={<Icon name="arrow_forward" size={20} />}
              >
                Send reset link
              </FormSubmit>
            </Form>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-small text-grey-900">
                Didn&apos;t receive it? Check spam, or{" "}
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="font-medium text-green-700 hover:text-green-600"
                >
                  send again
                </button>
                .
              </p>
            </div>
          )}

          <div className="flex items-center justify-center gap-1.5">
            <p className="text-small text-grey-900">Remembered it?</p>
            <Link
              to="/login"
              className="text-small font-medium text-green-700 hover:text-green-600"
            >
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
